import { Component, ViewEncapsulation, Input } from '@angular/core';
import * as _ from 'lodash';
import { GlobalsService } from '../../../shared/services/globals.service';
import { BaseGridUIComponent } from '../../../shared/base-components/base-grid-ui.component';
import { StatusHistoryBus } from '../../../shared/models/StatusHistoryBus';
import { InvoiceService } from '../../../shared/services/invoice.service';
import { BaseMessageInfo } from '../../../shared/models/BaseMessageInfo';

interface ConsolidatedRow {
    // Original fields from v_InvoiceLineHistory
    invoiceLineHistoryId: any;
    invoiceLineId: any;
    invoiceId: any;
    inventoryNumber: string;
    invoiceLineDesc: string;
    fullDescription: string;
    changeCodeDesc: string;
    resource: string;
    warehouseName: string;
    quantity: number | null;
    cost: number | null;
    totalCost: number | null;
    tax: number | null;
    markupCost: number | null;
    markup: number | null;
    sortOrder: number | null;
    price: number | null;
    amount: number | null;
    isPrintable: boolean;
    isTaxable: boolean;
    updatedBy: string;
    insertDt: Date;
    // Consolidation metadata
    isBaseline: boolean;
    changedFields: Set<string>;
}

@Component({
    selector: 'invoice-lineitems-history',
    templateUrl: './invoice-lineitems-history.component.html',
    styleUrls: ['./invoice-lineitems-history.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class InvoiceLineItemsHistoryComponent extends BaseGridUIComponent<StatusHistoryBus, null, InvoiceService> {

    constructor(globals: GlobalsService, svc_data: InvoiceService) {
        super(globals);
        this.svc_data = svc_data;
        this.allData = this.allData.bind(this);
    }

    _active = false;
    consolidatedRows: ConsolidatedRow[] = [];

    @Input() showInvoiceLineCost: boolean = true;

    public show(invoiceId, invoiceLineId) {
        this.gridData = [];
        this.consolidatedRows = [];
        this.dataLoading = true;
        this._active = true;
        this.svc_data.getLineItemHistory(invoiceId, invoiceLineId).subscribe((retData: BaseMessageInfo) => {
            if (retData.saveError) {
            } else {
                var ar = retData.primaryReturn;
                let i = 0;
                while (i < ar.length) {
                    ar[i].insertDt = new Date(ar[i].insertDt);
                    i++;
                }
                this.consolidatedRows = this.buildConsolidatedRows(ar);
            }
            this.dataLoading = false;
        });
    }

    statusNames: string[];

    public close() {
        this._active = false;
    }

    public processGridData() {
    }

    public isCellChanged(row: ConsolidatedRow, field: string): boolean {
        return !row.isBaseline && row.changedFields && row.changedFields.has(field);
    }

    public rowCallback = (context: { dataItem: ConsolidatedRow }) => {
        return { 'change-row': !context.dataItem.isBaseline };
    }

    private buildConsolidatedRows(entries: any[]): ConsolidatedRow[] {
        if (!entries || entries.length === 0) return [];

        // Sort chronologically for comparison
        entries.sort((a: any, b: any) => new Date(a.insertDt).getTime() - new Date(b.insertDt).getTime());

        const result: ConsolidatedRow[] = [];

        // First entry is the baseline
        result.push(this.toConsolidatedRow(entries[0], true, new Set<string>()));

        for (let i = 1; i < entries.length; i++) {
            const prev = entries[i - 1];
            const curr = entries[i];
            const changedFields = this.detectChanges(prev, curr);

            if (changedFields.size > 0) {
                result.push(this.toConsolidatedRow(curr, false, changedFields));
            }
        }

        // Newest on top
        return result.reverse();
    }

    private toConsolidatedRow(entry: any, isBaseline: boolean, changedFields: Set<string>): ConsolidatedRow {
        return {
            invoiceLineHistoryId: entry.invoiceLineHistoryId,
            invoiceLineId: entry.invoiceLineId,
            invoiceId: entry.invoiceId,
            inventoryNumber: entry.inventoryNumber || '',
            invoiceLineDesc: entry.invoiceLineDesc || '',
            fullDescription: entry.fullDescription || '',
            changeCodeDesc: entry.changeCodeDesc || '',
            resource: entry.resource || '',
            warehouseName: entry.warehouseName || '',
            quantity: entry.quantity != null ? Number(entry.quantity) : null,
            cost: entry.cost != null ? Number(entry.cost) : null,
            totalCost: entry.totalCost != null ? Number(entry.totalCost) : null,
            tax: entry.tax != null ? Number(entry.tax) : null,
            markupCost: entry.markupCost != null ? Number(entry.markupCost) : null,
            markup: entry.markup != null ? Number(entry.markup) : null,
            sortOrder: entry.sortOrder != null ? Number(entry.sortOrder) : null,
            price: entry.price != null ? Number(entry.price) : null,
            amount: entry.amount != null ? Number(entry.amount) : null,
            isPrintable: !!entry.isPrintable,
            isTaxable: !!entry.isTaxable,
            updatedBy: entry.updatedBy || '',
            insertDt: entry.insertDt,
            isBaseline: isBaseline,
            changedFields: changedFields
        };
    }

    private detectChanges(prev: any, curr: any): Set<string> {
        const changedFields = new Set<string>();
        const fieldsToCompare = [
            'inventoryNumber', 'invoiceLineDesc', 'fullDescription', 'changeCodeDesc',
            'resource', 'warehouseName', 'quantity', 'cost', 'totalCost', 'tax',
            'markupCost', 'markup', 'sortOrder', 'price', 'amount', 'isPrintable', 'isTaxable'
        ];

        for (const field of fieldsToCompare) {
            if (this.normalizeValue(prev[field]) !== this.normalizeValue(curr[field])) {
                changedFields.add(field);
            }
        }

        return changedFields;
    }

    private normalizeValue(val: any): string {
        if (val === null || val === undefined) return '__null__';
        if (typeof val === 'boolean') return val ? 'true' : 'false';
        if (typeof val === 'number') return val.toString();
        return val.toString().trim();
    }
}
